import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Project {
  id: string
  name: string
  status: string
  priority: string
  progress_percent: number
  budget: number
  spent: number
  team_size: number
  project_manager: string
  end_date: string
}

interface Task {
  id: string
  project_name: string
  title: string
  status: string
  priority: string
  assignee: string
  due_date: string
}

export default function ProjectsPage() {
  const [projects, setProjects] = useState<Project[]>([])
  const [tasks, setTasks] = useState<Task[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [projectsRes, tasksRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/projects/'),
      fetch(API_BASE + '/projects/tasks'),
      fetch(API_BASE + '/projects/statistics')
    ])
    if (projectsRes.ok) setProjects((await projectsRes.json()).projects || [])
    if (tasksRes.ok) setTasks((await tasksRes.json()).tasks || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': case 'done': return 'bg-green-100 text-green-800'
      case 'in_progress': return 'bg-blue-100 text-blue-800'
      case 'planning': case 'todo': return 'bg-gray-100 text-gray-800'
      case 'on_hold': case 'blocked': return 'bg-red-100 text-red-800'
      case 'review': return 'bg-purple-100 text-purple-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'text-red-600'
      case 'high': return 'text-orange-600'
      case 'medium': return 'text-yellow-600'
      default: return 'text-gray-600'
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(amount)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Project Management</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ New Project</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Projects</div>
              <div className="text-2xl font-bold">{statistics.total_projects}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Active</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.active_projects}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Tasks</div>
              <div className="text-2xl font-bold">{statistics.total_tasks}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Blocked</div>
              <div className="text-2xl font-bold text-red-600">{statistics.blocked_tasks}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Budget</div>
              <div className="text-2xl font-bold text-green-600">{formatCurrency(statistics.total_budget)}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Projects</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Project</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Progress</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Budget</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {projects.slice(0, 10).map(project => (
                  <tr key={project.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium">{project.name}</div>
                      <div className="text-xs text-gray-500">{project.project_manager} • {project.team_size} members</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="w-24 bg-gray-200 rounded-full h-2">
                          <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${project.progress_percent}%` }} />
                        </div>
                        <span className="text-sm">{project.progress_percent}%</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-medium">{formatCurrency(project.budget)}</div>
                      <div className="text-xs text-gray-500">Spent: {formatCurrency(project.spent)}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(project.status)}`}>
                        {project.status.replace(/_/g, ' ')}
                      </span>
                      <span className={`block text-xs mt-1 ${getPriorityColor(project.priority)}`}>{project.priority}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Recent Tasks</h3>
            <div className="space-y-3">
              {tasks.slice(0, 10).map(task => (
                <div key={task.id} className="p-3 bg-gray-50 rounded">
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-medium text-sm truncate flex-1">{task.title}</span>
                    <span className={`px-2 py-0.5 rounded text-xs ml-2 ${getStatusColor(task.status)}`}>{task.status}</span>
                  </div>
                  <div className="text-xs text-gray-500">{task.project_name}</div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-xs text-gray-600">{task.assignee}</span>
                    <span className={`text-xs ${getPriorityColor(task.priority)}`}>{task.priority}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
